import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { resolveOrganizationId } from "../../../Utils/organizationSession";
import StudentAttendanceViewDialog from "./StudentAttendanceViewDialog";
import { fetchFeeReceiptData } from "./feeReceiptUtils";
import { downloadEmsFeeReceiptPdf } from "./feeReceiptPdf";
import ProfileImagePreview from "../../../Components/Table/ProfileImagePreview";
import { User } from "lucide-react";

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "fees", label: "Fees" },
  { key: "courses", label: "Courses" },
  { key: "attendance", label: "Attendance" },
  { key: "certificates", label: "Certificates" },
  { key: "batch_assignment", label: "Batch Assignment" },
];

function moneyInr(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function resolveStudentPhotoUrl(value) {
  if (value == null || String(value).trim() === "") return null;
  let s = String(value).trim();
  if (s === "-" || s.toLowerCase() === "null") return null;

  s = s.replace(/\/storage\/students\//i, "/storage/app/public/students/");

  if (/^https?:\/\//i.test(s)) {
    try {
      const parsed = new URL(s);
      if (parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost") {
        return `${MAIN_URL}${parsed.pathname}${parsed.search}`;
      }
    } catch {
      /* keep absolute */
    }
    return s;
  }
  if (s.startsWith("/")) return `${MAIN_URL}${s}`;
  if (s.startsWith("storage/app/public/") || s.startsWith("students/")) {
    const path = s.startsWith("students/")
      ? `storage/app/public/${s}`
      : s;
    return `${MAIN_URL}/${path.replace(/^\/+/, "")}`;
  }
  if (s.startsWith("storage/")) {
    return `${MAIN_URL}/storage/app/public/${s.replace(/^storage\//, "")}`;
  }
  return `${MAIN_URL}/storage/app/public/students/${s.replace(/^\/+/, "")}`;
}

function formatDate(v) {
  if (!v || v === "-") return "—";
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function programName(admission) {
  return (
    admission?.training_program?.training_program_name ||
    admission?.training_program?.name ||
    admission?.training_program_name ||
    "—"
  );
}

function batchName(batchRow) {
  const batch = batchRow?.batch || batchRow?.Batch || batchRow;
  return (
    batch?.batch_code ||
    batch?.batch_name ||
    batchRow?.batch_code ||
    "—"
  );
}

function certName(cert) {
  return (
    cert?.certificate_number ||
    cert?.certificate_name ||
    cert?.name ||
    cert?.certificate_code ||
    "Certificate"
  );
}

function certHasFile(cert) {
  const url = cert?.certificate_url || cert?.file_url || cert?.url;
  return Boolean(url && String(url).trim() !== "");
}

async function downloadCertificateFile(certificateId, suggestedName, publicUrl) {
  const id = Number(certificateId);
  if (!id && !publicUrl) {
    throw new Error("Certificate file is not available.");
  }

  const headers = { ...getApiHeaders() };
  delete headers["Content-Type"];
  delete headers["content-type"];

  let arrayBuffer = null;
  let contentType = "";
  let disposition = "";

  if (id) {
    try {
      const res = await axios.get(
        `${MAIN_URL}/api/ems/certificates/${id}/download`,
        {
          headers,
          responseType: "arraybuffer",
          validateStatus: (status) => status >= 200 && status < 300,
        }
      );
      arrayBuffer = res.data;
      contentType = String(res.headers?.["content-type"] || "");
      disposition = String(res.headers?.["content-disposition"] || "");
    } catch {
      /* fall through to public URL */
    }
  }

  if (!arrayBuffer) {
    const { resolveCertificateUrl } = await import(
      "../../StudentPortal/useStudentPortalFetch"
    );
    const fallbackUrl = resolveCertificateUrl(publicUrl);
    if (!fallbackUrl) {
      throw new Error("Certificate file is not available.");
    }
    const fallbackRes = await fetch(fallbackUrl, {
      method: "GET",
      headers: {
        Authorization: headers.Authorization || "",
        "X-Application-Key": headers["X-Application-Key"] || "",
      },
    });
    if (!fallbackRes.ok) {
      throw new Error("Could not download certificate.");
    }
    arrayBuffer = await fallbackRes.arrayBuffer();
    contentType = fallbackRes.headers.get("content-type") || "";
    disposition = fallbackRes.headers.get("content-disposition") || "";
  }

  const bytes = new Uint8Array(arrayBuffer || []);
  const head = String.fromCharCode(...bytes.slice(0, 5));
  const looksLikePdf = head.startsWith("%PDF");
  const looksLikeJson =
    contentType.includes("application/json") ||
    head.startsWith("{") ||
    head.startsWith("[");
  if (!looksLikePdf && (looksLikeJson || bytes.length < 8)) {
    let message = "Certificate file is invalid or unavailable.";
    if (looksLikeJson) {
      try {
        message =
          JSON.parse(new TextDecoder().decode(bytes))?.message || message;
      } catch {
        /* ignore */
      }
    }
    throw new Error(message);
  }

  let fileName = suggestedName || "certificate";
  const match = String(disposition).match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
  );
  if (match) {
    fileName = decodeURIComponent(match[1] || match[2] || fileName);
  }
  if (!/\.\w+$/.test(fileName)) {
    fileName = `${fileName}${looksLikePdf ? ".pdf" : ""}`;
  }

  const blob = new Blob([arrayBuffer], {
    type: looksLikePdf
      ? "application/pdf"
      : contentType || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function EmptyState({ text }) {
  return (
    <Box
      sx={{
        py: 5,
        textAlign: "center",
        color: "var(--ems-faint, #94a3b8)",
        fontSize: 14,
      }}
    >
      {text}
    </Box>
  );
}

function unwrapList(payload) {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function extractStudentRelations(detail) {
  const admissions = Array.isArray(detail?.student_admissions)
    ? detail.student_admissions
    : Array.isArray(detail?.StudentAdmissions)
      ? detail.StudentAdmissions
      : [];
  const batchRows = Array.isArray(detail?.studnet_batches)
    ? detail.studnet_batches
    : Array.isArray(detail?.StudnetBatches)
      ? detail.StudnetBatches
      : Array.isArray(detail?.student_batches)
        ? detail.student_batches
        : [];
  return { admissions, batchRows };
}

function assessmentTypeOf(row) {
  return String(
    row?.assessment_type ||
      row?.assesment?.assessment_type ||
      row?.assessment?.assessment_type ||
      ""
  ).trim();
}

function assessmentNameOf(row) {
  return (
    row?.assessment_name ||
    row?.assesment?.assessment_name ||
    row?.assesment?.assesment_name ||
    row?.assessment?.assessment_name ||
    "—"
  );
}

function assessmentProgramOf(row) {
  return (
    row?.training_program?.training_program_name ||
    row?.assesment?.training_program?.training_program_name ||
    "—"
  );
}

function assessmentBatchOf(row) {
  return (
    row?.batch?.batch_name ||
    row?.batch?.batch_code ||
    row?.assesment?.batch?.batch_name ||
    row?.assesment?.batch?.batch_code ||
    "—"
  );
}

function resultScoreOf(row) {
  const v = row?.score_obtained ?? row?.score ?? row?.marks_obtained;
  return v === undefined || v === null || v === "" ? "—" : String(v);
}

function resultStatusOf(row) {
  return row?.result_status || row?.result || row?.grade || row?.status || "—";
}

function resultDateOf(row) {
  return (
    row?.assesment?.assessment_date ||
    row?.assessment?.assessment_date ||
    row?.assessment_date ||
    row?.updated_at ||
    row?.created_at
  );
}

function getInstallmentFees(inst) {
  if (Array.isArray(inst?.student_fees)) return inst.student_fees;
  if (Array.isArray(inst?.studentFees)) return inst.studentFees;
  return [];
}

function isDownloadableFeeReceipt(sf) {
  if (!sf?.organization_ems_student_fee_id) return false;
  const status = String(sf?.payment_status || "").trim().toLowerCase();
  if (!status) return true;
  return ["successful", "settled", "paid"].includes(status);
}

function receiptLabel(sf) {
  const no =
    sf?.payment_number ||
    sf?.receipt_number ||
    sf?.receipt_no ||
    null;
  return no != null && String(no).trim() !== "" ? String(no).trim() : "Receipt";
}

const nowrapCellSx = {
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

function assessmentIdOf(row) {
  const id =
    row?.organization_ems_assessment_id ??
    row?.assesment?.organization_ems_assessment_id ??
    row?.assessment?.organization_ems_assessment_id;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function mergeAssignmentWithResults(assignments, results) {
  const resultByAssessmentId = new Map();
  (Array.isArray(results) ? results : []).forEach((r) => {
    const id = assessmentIdOf(r);
    if (id == null) return;
    if (!resultByAssessmentId.has(id)) {
      resultByAssessmentId.set(id, r);
    }
  });

  return (Array.isArray(assignments) ? assignments : []).map((a, idx) => {
    const id = assessmentIdOf(a);
    const result = id != null ? resultByAssessmentId.get(id) || null : null;
    return {
      key: id != null ? `a-${id}` : `a-idx-${idx}`,
      assignment: a,
      result,
    };
  });
}

function CombinedAssessmentTable({ rows, emptyText }) {
  if (!rows.length) {
    return (
      <EmptyState
        text={emptyText || "No batch assignments found for this student."}
      />
    );
  }
  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 1100, tableLayout: "auto" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={nowrapCellSx}>Name</TableCell>
            <TableCell sx={nowrapCellSx}>Type</TableCell>
            <TableCell sx={nowrapCellSx}>Program</TableCell>
            <TableCell sx={nowrapCellSx}>Batch</TableCell>
            <TableCell sx={nowrapCellSx}>Max</TableCell>
            <TableCell sx={nowrapCellSx}>Score</TableCell>
            <TableCell sx={nowrapCellSx}>Result</TableCell>
            <TableCell sx={nowrapCellSx}>Student File</TableCell>
            <TableCell sx={nowrapCellSx}>Date</TableCell>
            <TableCell sx={nowrapCellSx}>Status</TableCell>
            <TableCell sx={nowrapCellSx}>Remarks</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ key, assignment: a, result: r }) => {
            const name = assessmentNameOf(a) || assessmentNameOf(r);
            const program = assessmentProgramOf(a);
            const dateVal = a?.assessment_date || resultDateOf(r);
            return (
              <TableRow key={key}>
                <TableCell sx={nowrapCellSx} title={name}>
                  {name}
                </TableCell>
                <TableCell sx={nowrapCellSx}>
                  {assessmentTypeOf(a) || assessmentTypeOf(r) || "—"}
                </TableCell>
                <TableCell sx={nowrapCellSx} title={program}>
                  {program}
                </TableCell>
                <TableCell sx={nowrapCellSx}>{assessmentBatchOf(a)}</TableCell>
                <TableCell sx={nowrapCellSx}>{a?.max_marks ?? a?.max_score ?? "—"}</TableCell>
                <TableCell sx={nowrapCellSx}>
                  {r ? resultScoreOf(r) : "—"}
                </TableCell>
                <TableCell sx={nowrapCellSx}>
                  {r ? resultStatusOf(r) : "—"}
                </TableCell>
                <TableCell sx={nowrapCellSx}>
                  {r?.submission_url ? (
                    <Box
                      component="a"
                      href={r.submission_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "primary.main",
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                      }}
                    >
                      {r.submission_name || "View file"}
                    </Box>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell sx={nowrapCellSx}>{formatDate(dateVal)}</TableCell>
                <TableCell sx={nowrapCellSx}>{a?.status || "—"}</TableCell>
                <TableCell sx={nowrapCellSx} title={r?.remarks || ""}>
                  {r?.remarks || "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function FieldGrid({ rows }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 1.5,
      }}
    >
      {rows.map((r) => (
        <Box
          key={r.label}
          sx={{
            border: "1px solid var(--ems-border, #e2e8f0)",
            borderRadius: 2,
            px: 1.5,
            py: 1.25,
            background: "var(--ems-surface, #fff)",
          }}
        >
          <Typography
            sx={{ fontSize: 11, color: "var(--ems-faint, #94a3b8)", mb: 0.35 }}
          >
            {r.label}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600, wordBreak: "break-word" }}>
            {r.value || "—"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function StudentDetailDialog({ open, student, onClose }) {
  const { userData } = useAuthStore();
  const [tab, setTab] = useState("profile");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [assessmentDataLoading, setAssessmentDataLoading] = useState(false);
  const [assessmentCatalog, setAssessmentCatalog] = useState([]);
  const [assessmentResults, setAssessmentResults] = useState([]);
  const [receiptDownloadingId, setReceiptDownloadingId] = useState(null);
  const [receiptEmailingId, setReceiptEmailingId] = useState(null);
  const [certDownloadingId, setCertDownloadingId] = useState(null);
  const [studentFeePayments, setStudentFeePayments] = useState([]);
  const [feesLoading, setFeesLoading] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);

  const studentId =
    student?.organization_ems_student_id || student?.id || null;
  const studentName = String(student?.student_name || "").trim() || "Student";
  const organizationId =
    student?.organization_id ||
    profile?.organization_id ||
    resolveOrganizationId(userData) ||
    localStorage.getItem("organization_id") ||
    null;

  const admissions = useMemo(() => {
    const fromStudent = Array.isArray(student?.student_admissions)
      ? student.student_admissions
      : [];
    if (fromStudent.length) return fromStudent;
    if (profile) return extractStudentRelations(profile).admissions;
    return [];
  }, [student, profile]);

  const installments = useMemo(
    () =>
      Array.isArray(student?.student_installments)
        ? student.student_installments
        : [],
    [student]
  );

  const batches = useMemo(() => {
    const fromStudent = Array.isArray(student?.studnet_batches)
      ? student.studnet_batches
      : Array.isArray(student?.student_batches)
        ? student.student_batches
        : [];
    if (fromStudent.length) return fromStudent;
    if (profile) return extractStudentRelations(profile).batchRows;
    return [];
  }, [student, profile]);

  const certificates = useMemo(
    () =>
      Array.isArray(student?.certficates)
        ? student.certficates
        : Array.isArray(student?.certificates)
          ? student.certificates
          : [],
    [student]
  );

  const combinedAssessmentRows = useMemo(
    () => mergeAssignmentWithResults(assessmentCatalog, assessmentResults),
    [assessmentCatalog, assessmentResults]
  );

  const feeRows = useMemo(() => {
    const paymentsByInstallment = new Map();
    studentFeePayments.forEach((sf) => {
      const instId = Number(sf?.organization_ems_fee_installment_id);
      if (!Number.isFinite(instId)) return;
      if (!paymentsByInstallment.has(instId)) {
        paymentsByInstallment.set(instId, []);
      }
      paymentsByInstallment.get(instId).push(sf);
    });

    return installments.map((inst) => {
      const instId = Number(inst?.organization_ems_fee_installment_id);
      const nested = getInstallmentFees(inst);
      const fetched = Number.isFinite(instId)
        ? paymentsByInstallment.get(instId) || []
        : [];
      const merged = [...nested];
      fetched.forEach((sf) => {
        const feeId = sf?.organization_ems_student_fee_id;
        if (
          feeId != null &&
          !merged.some(
            (x) => String(x?.organization_ems_student_fee_id) === String(feeId)
          )
        ) {
          merged.push(sf);
        }
      });
      return { ...inst, __fee_receipts: merged };
    });
  }, [installments, studentFeePayments]);

  useEffect(() => {
    if (!open) return;
    setTab("profile");
    setProfile(null);
    setAssessmentCatalog([]);
    setAssessmentResults([]);
    setStudentFeePayments([]);
    setReceiptDownloadingId(null);
    setCertDownloadingId(null);
    setPhotoFailed(false);
    setPhotoPreviewOpen(false);
  }, [open, studentId]);

  const loadProfile = useCallback(async () => {
    if (!open || !studentId) return;
    setProfileLoading(true);
    try {
      const res = await axios.get(`${MAIN_URL}/api/ems/students/${studentId}`, {
        headers: getApiHeaders(),
        params: organizationId ? { organization_id: organizationId } : undefined,
      });
      setProfile(res?.data?.data || res?.data || null);
    } catch {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, [open, studentId, organizationId]);

  const loadAssessmentData = useCallback(async () => {
    if (!open || !studentId) return;
    setAssessmentDataLoading(true);
    try {
      const res = await axios.get(
        `${MAIN_URL}/api/ems/students/${studentId}/batch-assignments`,
        {
          headers: getApiHeaders(),
          params: organizationId
            ? { organization_id: Number(organizationId) }
            : undefined,
        }
      );
      const payload = res?.data?.data || res?.data || {};
      const assignments = Array.isArray(payload.batch_assignments)
        ? payload.batch_assignments
        : [];
      const results = Array.isArray(payload.assignment_results)
        ? payload.assignment_results
        : [];
      setAssessmentCatalog(assignments);
      setAssessmentResults(results);

      if (res?.data?.success === false) {
        toast.error(
          res?.data?.message || "Could not load batch assignments."
        );
      }
    } catch (err) {
      setAssessmentCatalog([]);
      setAssessmentResults([]);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not load batch assignments.";
      toast.error(msg);
    } finally {
      setAssessmentDataLoading(false);
    }
  }, [open, studentId, organizationId]);

  const loadFeePayments = useCallback(async () => {
    if (!open || !studentId || !organizationId) return;
    setFeesLoading(true);
    try {
      const res = await axios.post(
        `${MAIN_URL}/api/ems/students-fees/filtered`,
        {
          organization_id: Number(organizationId),
          page: 1,
          pageSize: 200,
          organization_ems_student_id: Number(studentId),
          filters: [
            {
              field: "organization_ems_student_id",
              operator: "equals",
              value: Number(studentId),
            },
          ],
        },
        { headers: getApiHeaders() }
      );
      setStudentFeePayments(unwrapList(res?.data));
    } catch {
      setStudentFeePayments([]);
    } finally {
      setFeesLoading(false);
    }
  }, [open, studentId, organizationId]);

  const handleDownloadFeeReceipt = useCallback(
    async (studentFeeId, label) => {
      if (!studentFeeId) {
        toast.error("Receipt is not available for this installment.");
        return;
      }
      setReceiptDownloadingId(studentFeeId);
      try {
        const receiptData = await fetchFeeReceiptData(
          studentFeeId,
          organizationId
        );
        if (!receiptData) {
          toast.error("Could not load receipt data.");
          return;
        }
        const raw = label || receiptData.receiptNo || studentFeeId;
        const safe = String(raw).replace(/[^\w.-]+/g, "_");
        await downloadEmsFeeReceiptPdf(receiptData, `fee-receipt-${safe}.pdf`);
        toast.success("Receipt downloaded.");
      } catch (err) {
        console.error("Fee receipt PDF failed:", err);
        toast.error("Could not download receipt.");
      } finally {
        setReceiptDownloadingId(null);
      }
    },
    [organizationId]
  );

  const handleEmailFeeReceipt = useCallback(
    async (studentFeeId) => {
      if (!studentFeeId) {
        toast.error("Receipt is not available to email.");
        return;
      }
      setReceiptEmailingId(studentFeeId);
      try {
        const res = await axios.post(
          `${MAIN_URL}/api/ems/students-fees/${studentFeeId}/send-email`,
          { organization_id: organizationId ? Number(organizationId) : undefined },
          { headers: getApiHeaders() }
        );
        if (res?.data?.success === false) {
          toast.error(res?.data?.message || "Could not send fee receipt email.");
          return;
        }
        toast.success(res?.data?.message || "Fee receipt email sent.");
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Could not send fee receipt email.";
        toast.error(msg);
      } finally {
        setReceiptEmailingId(null);
      }
    },
    [organizationId]
  );

  const handleDownloadCertificate = useCallback(async (cert) => {
    const certificateId = cert?.organization_ems_certificate_id;
    if (!certificateId && !certHasFile(cert)) {
      toast.error("Certificate file is not available.");
      return;
    }
    setCertDownloadingId(certificateId || "file");
    try {
      const suggested = String(
        cert?.certificate_number ||
          cert?.certificate_name ||
          "certificate"
      ).replace(/[^\w.-]+/g, "_");
      await downloadCertificateFile(
        certificateId,
        suggested,
        cert?.certificate_url
      );
      toast.success("Certificate downloaded.");
    } catch (err) {
      console.error("Certificate download failed:", err);
      toast.error(err?.message || "Could not download certificate.");
    } finally {
      setCertDownloadingId(null);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (tab === "profile") loadProfile();
    if (tab === "fees") loadFeePayments();
    if (tab === "batch_assignment") {
      loadAssessmentData();
    }
  }, [open, tab, loadProfile, loadFeePayments, loadAssessmentData]);

  const profileSource = profile || student || {};
  const photoUrl = resolveStudentPhotoUrl(
    profileSource.profile_image_url || student?.profile_image_url
  );
  const showPhoto = Boolean(photoUrl) && !photoFailed;

  useEffect(() => {
    setPhotoFailed(false);
  }, [photoUrl]);

  const profileRows = [
    { label: "Gender", value: profileSource.gender || student?.gender },
    { label: "Date of Birth", value: formatDate(profileSource.date_of_birth || student?.date_of_birth) },
    { label: "Phone", value: profileSource.phone || student?.phone },
    { label: "Alternate Phone", value: profileSource.alternate_phone || student?.alternate_phone },
    { label: "Email", value: profileSource.email || student?.email },
    { label: "Country", value: profileSource?.country?.country_name || student?.country },
    { label: "State", value: profileSource?.state?.state_name || student?.state },
    { label: "City", value: profileSource?.city?.city_name || student?.city },
    { label: "Address", value: profileSource.address || student?.address },
    { label: "Status", value: profileSource.student_status || student?.student_status },
  ];

  const renderBatchAssignmentTab = () => {
    if (assessmentDataLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    return (
      <CombinedAssessmentTable
        rows={combinedAssessmentRows}
        emptyText="No batch assignments found for this student’s batches."
      />
    );
  };

  return (
    <>
      <Dialog
        open={Boolean(open)}
        onClose={onClose}
        maxWidth={
          tab === "attendance" || tab === "batch_assignment" ? "lg" : "md"
        }
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
            pb: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
              {studentName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Student overview
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box
          className="ems-notif-tabs"
          sx={{
            px: 1.5,
            borderTop: "1px solid var(--ems-border, #e2e8f0)",
            borderBottom: "1px solid var(--ems-border, #e2e8f0)",
            overflowX: "auto",
            display: "flex",
            alignItems: "stretch",
            gap: 0,
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { height: 4 },
          }}
        >
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <Box
                key={t.key}
                component="button"
                type="button"
                className={`ems-notif-tab${active ? " active" : ""}`}
                onClick={() => setTab(t.key)}
                sx={{
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  border: "none !important",
                  outline: "none !important",
                  boxShadow: "none !important",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: 12.5,
                  fontWeight: 650,
                  color: active
                    ? "var(--ems-primary, #0d9488)"
                    : "var(--ems-muted, #64748b)",
                  px: 1.5,
                  py: 1.25,
                  position: "relative",
                  lineHeight: 1.2,
                  "&:hover": {
                    color: active
                      ? "var(--ems-primary, #0d9488)"
                      : "var(--ems-text, #0f172a)",
                  },
                  "&::after": active
                    ? {
                        content: '""',
                        position: "absolute",
                        left: 10,
                        right: 10,
                        bottom: 0,
                        height: "2.5px",
                        borderRadius: "2px 2px 0 0",
                        background: "var(--ems-primary, #0d9488)",
                      }
                    : undefined,
                }}
              >
                {t.label}
              </Box>
            );
          })}
        </Box>

        <DialogContent
          sx={{
            pt: 2.5,
            minHeight: tab === "attendance" ? 420 : 320,
            overflow: "auto",
          }}
        >
          {tab === "profile" && (
            profileLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    pb: 0.5,
                  }}
                >
                  <Box
                    onClick={() => {
                      if (showPhoto) setPhotoPreviewOpen(true);
                    }}
                    title={showPhoto ? "Click to preview photo" : undefined}
                    role={showPhoto ? "button" : undefined}
                    tabIndex={showPhoto ? 0 : undefined}
                    onKeyDown={
                      showPhoto
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setPhotoPreviewOpen(true);
                            }
                          }
                        : undefined
                    }
                    sx={{
                      width: 88,
                      height: 88,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "1px solid var(--ems-border, #e2e8f0)",
                      background: "var(--ems-surface-2, #f1f5f9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      cursor: showPhoto ? "zoom-in" : "default",
                      transition: "transform 0.15s ease, box-shadow 0.15s ease",
                      ...(showPhoto
                        ? {
                            "&:hover": {
                              transform: "scale(1.04)",
                              boxShadow: "0 0 0 2px rgba(13, 148, 136, 0.35)",
                            },
                          }
                        : {}),
                    }}
                  >
                    {showPhoto ? (
                      <Box
                        component="img"
                        src={photoUrl}
                        alt={studentName}
                        onError={() => setPhotoFailed(true)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <User size={36} color="#94a3b8" strokeWidth={1.75} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                      {studentName}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}
                    >
                      {profileSource.student_id || student?.student_id || "—"}
                    </Typography>
                  </Box>
                </Box>
                <FieldGrid rows={profileRows} />
              </Box>
            )
          )}

          {tab === "fees" && (
            feesLoading && installments.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : feeRows.length === 0 ? (
              <EmptyState text="No fee installments found for this student." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Receipt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feeRows.map((inst, idx) => {
                    const feeReceipts = (
                      Array.isArray(inst.__fee_receipts)
                        ? inst.__fee_receipts
                        : getInstallmentFees(inst)
                    ).filter(isDownloadableFeeReceipt);
                    return (
                      <TableRow
                        key={
                          inst?.organization_ems_fee_installment_id ||
                          inst?.organization_ems_student_fee_id ||
                          idx
                        }
                      >
                        <TableCell>
                          {formatDate(inst?.due_date || inst?.fee_due_date)}
                        </TableCell>
                        <TableCell>
                          {moneyInr(
                            inst?.fee_inr ??
                              inst?.amount_inr ??
                              inst?.amount ??
                              inst?.fee_instalment_inr
                          )}
                        </TableCell>
                        <TableCell>
                          {inst?.payment_status || inst?.status || "—"}
                        </TableCell>
                        <TableCell>
                          {feeReceipts.length === 0 ? (
                            feesLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              "—"
                            )
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.75,
                                alignItems: "center",
                              }}
                            >
                              {feeReceipts.map((sf) => {
                                const feeId =
                                  sf.organization_ems_student_fee_id;
                                const label = receiptLabel(sf);
                                const busyDownload =
                                  String(receiptDownloadingId) ===
                                  String(feeId);
                                const busyEmail =
                                  String(receiptEmailingId) === String(feeId);
                                return (
                                  <Box
                                    key={feeId}
                                    sx={{
                                      display: "inline-flex",
                                      gap: 0.25,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Tooltip title="Download receipt PDF">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          aria-label={`Download receipt ${label}`}
                                          disabled={busyDownload || busyEmail}
                                          onClick={() =>
                                            handleDownloadFeeReceipt(
                                              feeId,
                                              label
                                            )
                                          }
                                        >
                                          {busyDownload ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <DownloadIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Email receipt to student">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="secondary"
                                          aria-label={`Email receipt ${label}`}
                                          disabled={busyDownload || busyEmail}
                                          onClick={() =>
                                            handleEmailFeeReceipt(feeId)
                                          }
                                        >
                                          {busyEmail ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <EmailIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )
          )}

          {tab === "courses" && (
            admissions.length === 0 && batches.length === 0 ? (
              <EmptyState text="No courses or batches found for this student." />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {admissions.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>
                      Admissions
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Program</TableCell>
                          <TableCell>Admission Date</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {admissions.map((a, idx) => (
                          <TableRow key={a?.organization_ems_admission_id || idx}>
                            <TableCell>{programName(a)}</TableCell>
                            <TableCell>{formatDate(a?.admission_date)}</TableCell>
                            <TableCell>
                              {a?.admission_status || a?.status || "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                {batches.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>
                      Batches
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Batch</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {batches.map((b, idx) => (
                          <TableRow key={b?.organization_ems_batch_student_id || idx}>
                            <TableCell>{batchName(b)}</TableCell>
                            <TableCell>
                              {b?.batch_status ||
                                b?.enrollment_status ||
                                b?.batch?.status ||
                                b?.status ||
                                "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            )
          )}

          {tab === "attendance" && (
            studentId ? (
              <StudentAttendanceViewDialog
                open
                embedded
                studentId={studentId}
                studentName={studentName}
                studentCode={
                  profileSource.student_id || student?.student_id || ""
                }
                onClose={() => {}}
              />
            ) : (
              <EmptyState text="Student not found for attendance." />
            )
          )}

          {tab === "certificates" && (
            certificates.length === 0 ? (
              <EmptyState text="No certificates found for this student." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Certificate</TableCell>
                    <TableCell>Issue Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {certificates.map((c, idx) => {
                    const certId = c?.organization_ems_certificate_id;
                    const canDownload = Boolean(certId) || certHasFile(c);
                    const busy =
                      certId != null &&
                      String(certDownloadingId) === String(certId);
                    return (
                      <TableRow key={certId || idx}>
                        <TableCell>{certName(c)}</TableCell>
                        <TableCell>
                          {formatDate(c?.issue_date || c?.certificate_issue_date)}
                        </TableCell>
                        <TableCell>
                          {c?.status || c?.certificate_status || "—"}
                        </TableCell>
                        <TableCell align="right">
                          {canDownload ? (
                            <Tooltip title="Download certificate">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label="Download certificate"
                                  disabled={busy}
                                  onClick={() => handleDownloadCertificate(c)}
                                >
                                  {busy ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DownloadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )
          )}

          {tab === "batch_assignment" && renderBatchAssignmentTab()}
        </DialogContent>
      </Dialog>
      <ProfileImagePreview
        open={photoPreviewOpen}
        src={photoUrl}
        alt={studentName}
        onClose={() => setPhotoPreviewOpen(false)}
      />
      {/* Attendance is rendered embedded inside the Attendance tab */}
    </>
  );
}
